import {useLayoutEffect, useRef} from 'react';
import {styled, css} from 'styled-components';
import {createPortal} from 'react-dom';
import {atom, useAtomValue, useSetAtom} from 'jotai';
import {wiwAtom} from 'r/atoms';
import {reactElement, insertStyle, getUniqueNumber} from 'r/utils';
import {func, bool} from 'prop-types';
import ButtonContainer from 'r/ui/ButtonContainer';
import {zModalsWr} from 'r/constants/zIdxs';
import {forbidExtraProps} from 'airbnb-prop-types';
import {bgAccentMain, textSecondary} from 'r/Design';
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import svg from 'r/icons/cancel.svg';


const Wr = styled.div`
  position: absolute;
  z-index: ${zModalsWr};
  left: 0;
  top: 0;
  right: 0;
  min-height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
  overflow-x: hidden;
  display: ${props => props.$isHidden ? 'none' : 'flex'};
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: ${bgAccentMain};
    opacity: 0.7;
  }
`;

const Content = styled.div`
  z-index: 1;
  background: ${bgAccentMain};
  box-shadow: 0px 4px 18px rgba(0, 0, 0, 0.25);
  max-width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-flow: column;
  ${props => props.$fullScreen && css`
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    overflow: auto;
  `};
`;

const CloseCross = styled(ButtonContainer)`
  position: absolute;
  z-index: 1;
  right: 16px;
  top: 16px;
  width: 32px;
  height: 32px;
  background-color: ${textSecondary};
  mask: url("${svg}") no-repeat center;
  mask-size: 32px 32px;
  mask-type: cover;
  ${props => props.$fullScreen && css`
    right: 36px;
    top: 40px;
    width: 24px;
    height: 24px;
    @media (max-width: 374px) {
      top: 32px;
      right: 32px;
    }
  `};
`;

const modalsAtom = atom([]);
const removeModalAtom = atom(null, (get, set, _id) => {
  set(modalsAtom, get(modalsAtom).filter(({id}) => id !== _id));
});
const addModalAtom = atom(null, (get, set, {prevModalScrollTop, ...modal}) => {
  const prevModal = get(modalsAtom).slice(-1)[0];
  if (prevModal) {
    prevModal.scrollTop = prevModalScrollTop;
  }
  set(modalsAtom, [...get(modalsAtom), modal]);
});


export default Modal;

Modal.propTypes = forbidExtraProps({
  closeModal: func,
  children: reactElement.isRequired,
  fullScreen: bool,
});

function Modal({
  closeModal,
  children,
  fullScreen,
}) {
  const modals = useAtomValue(modalsAtom);
  const addModal = useSetAtom(addModalAtom);
  const removeModal = useSetAtom(removeModalAtom);
  const wiw = useAtomValue(wiwAtom);
  fullScreen = typeof fullScreen === 'boolean' ? fullScreen : wiw <= 767;

  const modalsRef = useRef(modals);
  useLayoutEffect(() => {
    modalsRef.current = modals;
  }, [modals]);

  const idRef = useRef(getUniqueNumber());
  const wrRef = useRef();
  useLayoutEffect(() => {
    const [lastModal] = modals.slice(-1);
    const rootScrollTop = lastModal ? lastModal.rootScrollTop : window.scrollY;
    const prevModalScrollTop = window.scrollY;
    const needScrollbar = lastModal ? lastModal.needScrollbar : document.body.scrollHeight > window.innerHeight;
    addModal({rootScrollTop, id: idRef.current, prevModalScrollTop});
    const cleanStyle = insertStyle(fullScreen
      ? `
          body {
            position: absolute;
            height: 100%;
            width: 100%;
          }
        `
      : `
      ${needScrollbar ? 'body {overflow-y: scroll;}' : ''}
      #root {
        position: fixed;
        top: -${rootScrollTop}px;
      }
    `);
    if (fullScreen) {
      disableBodyScroll(wrRef.current, {allowTouchMove: target => wrRef.current.contains(target)});
    }
    window.scrollTo(0, 0);
    return () => {
      removeModal(idRef.current);
      if (fullScreen) {
        enableBodyScroll(wrRef.current);
      }
      setTimeout(() => {
        cleanStyle();
        if (modalsRef.current.slice(-1)[0].id === idRef.current) {
          window.scrollTo(0, modalsRef.current.length === 1 ? rootScrollTop : modalsRef.current.slice(-2)[0].scrollTop);
        }
      }, 0);
    };
  }, []);


  const isOverlayPressedRef = useRef();

  return createPortal(
    <Wr
      ref={wrRef}
      $isHidden={modals.slice(-1)[0]?.id !== idRef.current}
      onMouseDown={event => {
        event.stopPropagation();
        const {target, currentTarget} = event;
        if (target !== currentTarget) {
          return;
        }
        isOverlayPressedRef.current = true;
      }}
      onMouseUp={({target, currentTarget}) => {
        if (target !== currentTarget) {
          return;
        }
        if (!isOverlayPressedRef.current) {
          return;
        }
        closeModal?.();
      }}
    >
      <Content $fullScreen={fullScreen}>
        {closeModal && <CloseCross onClick={() => closeModal()}/>}
        {children}
      </Content>
    </Wr>,
    document.querySelector('body'),
  );
}
